import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { FileText, CheckCircle2, AlertTriangle, Clock, Sparkles } from "lucide-react";

export default function AdminExamsPage() {
  const [exams, setExams] = useState([]);
  const [filter, setFilter] = useState("pending_review");
  const [reviewing, setReviewing] = useState(null);
  const [altered, setAltered] = useState(false);
  const [observations, setObservations] = useState("");
  const [params] = useSearchParams();
  const highlightId = params.get("highlight");

  const load = async () => {
    const q = filter ? `?status=${filter}` : "";
    try {
      const { data } = await api.get(`/admin/exams${q}`);
      setExams(data);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Falha ao carregar."); }
  };

  useEffect(() => { load(); }, [filter]); // eslint-disable-line

  const openReview = (ex) => {
    setReviewing(ex);
    setAltered(ex.altered || false);
    setObservations(ex.observations || "");
  };

  const submit = async () => {
    try {
      await api.post(`/admin/exams/${reviewing.id}/review`, { altered, observations });
      toast.success("Exame analisado.");
      setReviewing(null);
      await load();
    } catch (_) { toast.error("Erro ao registrar análise."); }
  };

  return (
    <div className="space-y-6" data-testid="admin-exams-page">
      <div>
        <div className="overline text-accent mb-3">Exames</div>
        <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight">Análise laboratorial</h1>
      </div>

      <div className="flex gap-2">
        {[
          { v: "pending_review", l: "Pendentes" },
          { v: "reviewed", l: "Analisados" },
          { v: "", l: "Todos" },
        ].map((o) => (
          <button
            key={o.v}
            onClick={() => setFilter(o.v)}
            className={`px-3 py-1.5 rounded-sm text-sm font-medium transition-colors ${filter === o.v ? "bg-primary text-primary-foreground" : "bg-card border border-border hover:bg-muted"}`}
            data-testid={`exam-filter-${o.v || "all"}`}
          >
            {o.l}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {exams.map((ex) => (
          <div
            key={ex.id}
            className={`bg-card border rounded-sm p-5 ${highlightId === ex.id ? "border-primary ring-2 ring-primary/30" : "border-border"}`}
            data-testid={`admin-exam-${ex.id}`}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-primary" />
                <span className="font-heading font-semibold text-sm">{ex.exam_type}</span>
              </div>
              {ex.status === "pending_review" && <span className="text-xs text-warning inline-flex items-center gap-1 overline"><Clock className="w-3 h-3" /> Pendente</span>}
              {ex.status === "reviewed" && !ex.altered && <span className="text-xs text-success inline-flex items-center gap-1 overline"><CheckCircle2 className="w-3 h-3" /> Normal</span>}
              {ex.altered && <span className="text-xs text-destructive inline-flex items-center gap-1 overline"><AlertTriangle className="w-3 h-3" /> Alterado</span>}
            </div>
            <div className="text-xs text-muted-foreground truncate">{ex.patient_name}</div>
            <div className="text-xs text-muted-foreground mt-1">{new Date(ex.created_at).toLocaleDateString("pt-BR")}</div>
            {ex.observations && <div className="text-xs mt-2 p-2 bg-muted rounded-sm">{ex.observations}</div>}
            <div className="mt-4 flex gap-2">
              <a
                href={`${api.defaults.baseURL}/admin/exams/${ex.id}/download`}
                target="_blank" rel="noreferrer"
                className="flex-1 text-center px-3 py-1.5 rounded-sm border border-border text-xs font-semibold hover:bg-muted transition-colors"
                data-testid={`exam-download-${ex.id}`}
              >
                Abrir arquivo
              </a>
              <Link
                to={`/admin/exames/${ex.id}/analisar`}
                className="flex-1 inline-flex items-center justify-center gap-1 rounded-sm bg-primary text-primary-foreground px-3 py-1.5 text-xs font-semibold hover:bg-primary/90 transition-colors"
                data-testid={`exam-analyze-${ex.id}`}
              >
                <Sparkles className="w-3 h-3" />
                {ex.status === "reviewed" ? "Rever análise" : "Analisar com IA"}
              </Link>
              <button
                onClick={() => openReview(ex)}
                className="rounded-sm border border-border px-2 py-1.5 text-xs font-semibold hover:bg-muted transition-colors"
                data-testid={`exam-review-${ex.id}`}
                title="Análise manual (marcar como revisado sem IA)"
              >
                Manual
              </button>
            </div>
          </div>
        ))}
        {exams.length === 0 && (
          <div className="md:col-span-3 bg-card border border-border rounded-sm p-10 text-center text-muted-foreground">Nenhum exame neste filtro.</div>
        )}
      </div>

      {reviewing && (
        <div className="fixed inset-0 bg-black/50 z-50 grid place-items-center p-4" onClick={() => setReviewing(null)} data-testid="review-modal">
          <div className="bg-card border border-border rounded-sm p-6 max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
            <div className="overline text-muted-foreground mb-2">Análise farmacêutica</div>
            <h3 className="font-heading text-lg font-semibold">{reviewing.exam_type}</h3>
            <p className="text-xs text-muted-foreground">{reviewing.patient_name}</p>
            <div className="mt-5 space-y-4">
              <label className="flex items-center gap-3">
                <input type="checkbox" checked={altered} onChange={(e) => setAltered(e.target.checked)} className="accent-destructive w-4 h-4" data-testid="altered-checkbox" />
                <span className="text-sm">Marcar como alterado</span>
              </label>
              <div>
                <label className="overline text-muted-foreground mb-1 block">Observações clínicas</label>
                <textarea rows={5} value={observations} onChange={(e) => setObservations(e.target.value)} className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm" data-testid="observations-input" />
              </div>
              <div className="flex gap-2 justify-end">
                <button onClick={() => setReviewing(null)} className="px-4 py-2 rounded-sm border border-border text-sm hover:bg-muted transition-colors" data-testid="cancel-review">Cancelar</button>
                <button onClick={submit} className="px-4 py-2 rounded-sm bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors" data-testid="save-review">Salvar análise</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
